import { inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import BrowserTabsLock from 'browser-tabs-lock';
import { firstValueFrom } from 'rxjs';

/**
 * Lock key used by auth0-spa-js v1 to serialise silent-authentication iframes.
 *
 * This app is hosted inside a portal page that also loads sibling micro-frontends still
 * running auth0-spa-js v1. Every one of those SDKs opens a hidden iframe against the same
 * Auth0 domain and listens for the `authorization_response` postMessage on the shared top
 * window. The SDK listener only filters on `event.origin` and `event.data.type` - never on
 * `event.source` - so whenever two iframes overlap, the first response delivered is consumed
 * by *every* listener and the losers fail with `state_mismatch` / "Invalid state".
 *
 * auth0-spa-js v1 prevented the overlap by holding this single localStorage-backed lock for
 * the whole silent-auth flow. auth0-spa-js v2 (pulled in by @auth0/auth0-angular v2) switched
 * to the Web Locks API with `clientId`/`audience` scoped keys, so it no longer excludes the v1
 * siblings. Taking the legacy lock ourselves restores page-wide serialisation.
 */
const LEGACY_SILENT_AUTH_LOCK_KEY = 'auth0.lock.getTokenSilently';

const LOCK_TIMEOUT_MS = 5000;
const MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 400;
const RETRY_JITTER_MS = 400;

/**
 * Detects the auth0-spa-js error raised when an iframe response belonging to another
 * application on the page is delivered to our listener.
 */
export function isSilentAuthStateMismatch(error: unknown): boolean {
  const candidate = error as { error?: string; message?: string } | null;

  return !!candidate && (candidate.error === 'state_mismatch' || candidate.message === 'Invalid state');
}

/**
 * Delay before re-running silent authentication. An immediate retry fails just as often,
 * because the orphaned `authorization_response` from the previous attempt is still queued and
 * gets picked up by the freshly attached listener.
 */
export function silentAuthRetryDelay(attempt: number): number {
  return RETRY_BASE_DELAY_MS * attempt + Math.floor(Math.random() * RETRY_JITTER_MS);  // NOSONAR
}

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * App initializer that warms the Auth0 token cache while holding the page-wide silent-auth
 * lock. Once the cache is populated, `AuthHttpInterceptor` resolves tokens without opening a
 * second iframe, so the API call that renders the page can no longer lose the race.
 *
 * Failures are swallowed: bootstrapping must never be blocked by token acquisition.
 */
export function primeAccessToken(): Promise<void> {
  const authService = inject(AuthService);

  return acquireTokenUnderPageLock(authService);
}

async function acquireTokenUnderPageLock(authService: AuthService): Promise<void> {
  const lock = new BrowserTabsLock();
  const acquired = await lock.acquireLock(LEGACY_SILENT_AUTH_LOCK_KEY, LOCK_TIMEOUT_MS);

  try {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        await firstValueFrom(authService.getAccessTokenSilently());
        return;
      } catch (error) {
        if (attempt === MAX_ATTEMPTS || !isSilentAuthStateMismatch(error)) {
          return;
        }

        await wait(silentAuthRetryDelay(attempt));
      }
    }
  } finally {
    if (acquired) {
      await lock.releaseLock(LEGACY_SILENT_AUTH_LOCK_KEY);
    }
  }
}
